import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { Box, Button, Divider, IconButton, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslate } from 'react-admin';

interface SectionHeaderProps {
  /** Context zone only — pill tabs, a scope/date row, or an inline search. */
  left?: ReactNode;
  /** Extra toggles that belong before the divider (metadata, bulk actions). */
  beforeDivider?: ReactNode;
  onHelp?: () => void;
  /** The `FiltersPopover` element, when this screen has more than one filter. */
  filters?: ReactNode;
  onRefresh?: () => void;
  onExport?: () => void;
  onCreate?: () => void;
  createLabel?: string;
  /** Screen-specific actions appended after Create. */
  after?: ReactNode;
}

/**
 * Borderless title/toolbar row sitting on the canvas (hybrid-3c.md v3.6) —
 * not a white card; only the table below a screen reads as a card. Sticky
 * so it masks content scrolling underneath. The right cluster keeps a fixed
 * order on every screen: Help → Filters → Refresh → divider → Export →
 * Create — all rendered through the single `MuiButton` system (v3.8), no
 * ad-hoc pill sizing.
 */
export function SectionHeader({
  left,
  beforeDivider,
  onHelp,
  filters,
  onRefresh,
  onExport,
  onCreate,
  createLabel,
  after,
}: SectionHeaderProps) {
  const translate = useTranslate();
  const hasLeftGroup = Boolean(onHelp || filters || onRefresh || beforeDivider);
  const hasRightGroup = Boolean(onExport || onCreate);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 0.5,
        py: 1.5,
        backgroundColor: 'background.default',
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>{left}</Box>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        {beforeDivider}
        {onHelp && (
          <IconButton size="small" onClick={onHelp} aria-label={translate('pay.sectionHeader.help')}>
            <HelpOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        )}
        {filters}
        {onRefresh && (
          <IconButton size="small" onClick={onRefresh} aria-label={translate('pay.sectionHeader.refresh')}>
            <RefreshOutlinedIcon fontSize="small" />
          </IconButton>
        )}

        {hasLeftGroup && hasRightGroup && (
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        )}

        {onExport && (
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            onClick={onExport}
          >
            {translate('pay.sectionHeader.export')}
          </Button>
        )}
        {onCreate && (
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon fontSize="small" />}
            onClick={onCreate}
          >
            {createLabel ?? translate('pay.sectionHeader.create')}
          </Button>
        )}
        {after}
      </Stack>
    </Box>
  );
}
